import React from 'react';
import { View, FlatList } from 'react-native';
import GestureRecognizer from 'react-native-swipe-gestures';
import OutsidePressHandler from 'react-native-outside-press';

import { scale } from '@components/frame';
import { isPhone, isWeb } from '@helpers';

import { getBoxShadowStyle } from '@styles/shadow';

import Header from './header';
import Popup from './popup';
import BarChart from './barChart';
import LineChart from './lineChart';
import { connect } from 'react-redux';
import { setChartType } from '@reducers/params';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import ChartLegends from './ChartLegends';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { useManageChart } from '@components/machines/components/eventsChart/hooks/useManageChart';

const Chart = (props) => {
  const {
    C,
    fullWidth,
    fullHeight,
    setSelectedIndex,
    setSelectedItem,
    selectedIndex,
    changeDate,
    showLine,
    isActive,
    isDay,
  } = props;

  const {
    flatListRef,
    padding,
    showInitialLoader,
    shouldDisplayLegends,
    IS_NARROW_WEB,
    chartHorizontalOffset,
    flatListItemWidth,
    legendsWidth,
    chartHeight,
    chartWidth,
    triggerLine,
    config,
    renderChart,
    renderChartPrev,
    saveChartData,
    saveChartDataPrev,
    selectedFlatListIndex,
    userStopScroll,
    dataBar,
    heightValues,
    readyToDisplayLegends,
    loaderContainerStyles,
  } = useManageChart(props);

  return (
    <TourTarget tourId={WELCOME_TOUR_IDS.PERFORMANCE_CHART}>
    <View
      testID={TEST_IDS.chart.outerContainer}
      style={[
        {
          borderRadius: scale(SCREEN_BORDER_RADIUS),
        },
        isPhone &&
          !IS_NARROW_WEB && {
            overflow: 'hidden',
          },
      ]}
    >
      <View
        testID={TEST_IDS.chart.innerContainer}
        style={[
          getBoxShadowStyle({ type: 'Field', C }),
          {
            width: fullWidth,
            height: fullHeight,
            backgroundColor: C('Background/Light/Field'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
          },
        ]}
      >
        <View style={[!shouldDisplayLegends && { paddingHorizontal: padding }]}>
          <View
            style={[
              shouldDisplayLegends && {
                paddingHorizontal: padding,
              },
            ]}
          >
            {selectedIndex !== null ? (
              <Popup {...{ ...props, triggerLine }} />
            ) : isWeb ? (
              <GestureRecognizer onSwipeLeft={triggerLine} onSwipeRight={triggerLine} config={config}>
                <Header
                  {...{
                    ...props,
                    setSelectedChartDate: props.setSelectedChartDateFromCalendar,
                    triggerLine,
                  }}
                />
              </GestureRecognizer>
            ) : (
              <Header
                {...{
                  ...props,
                  data: renderChart[selectedFlatListIndex]?.data,
                  dataPrev: renderChartPrev[selectedFlatListIndex]?.data,
                  selectedChartDate: renderChart[selectedFlatListIndex]?.date,
                  setSelectedChartDate: props.setSelectedChartDateFromCalendar,
                  triggerLine,
                  loading: !showLine && showInitialLoader,
                }}
              />
            )}
          </View>
          {isWeb && (
            <GestureRecognizer onSwipeLeft={() => changeDate('add')} onSwipeRight={() => changeDate('subtract')} config={config}>
              {showLine ? (
                <LineChart {...{ ...props }} />
              ) : (
                <OutsidePressHandler
                  onOutsidePress={() => {
                    const clear = () => {
                      setSelectedIndex(null);
                      setSelectedItem(null);
                    };

                    if (isWeb) {
                      clear();
                    } else {
                      setTimeout(() => {
                        clear();
                      }, 500);
                    }
                  }}
                >
                  <BarChart
                    {...{
                      ...props,
                      selectedChartDate: renderChart[selectedFlatListIndex]?.date || props.selectedChartDate,
                      renderLegends: true,
                      chartHeight,
                      chartWidth,
                    }}
                  />
                </OutsidePressHandler>
              )}
            </GestureRecognizer>
          )}
          {!isWeb && (
            <View testID={TEST_IDS.chart.mobileFlatListContainer} style={[{ width: flatListItemWidth, flexDirection: 'row' }, shouldDisplayLegends && { marginLeft: padding }]}>
              <FlatList
                ref={flatListRef}
                data={renderChart}
                renderItem={({ item, index }) => (
                  <View style={{ width: flatListItemWidth - legendsWidth }}>
                    {showLine ? (
                      <LineChart
                        {...{
                          ...props,
                          data: item.data,
                          setData: (newData) => {
                            props.setData(newData);
                            saveChartData(item.date, newData);
                          },
                          dataPrev: renderChartPrev[index]?.data,
                          setDataPrev: (newDataPrev) => {
                            props.setDataPrev(newDataPrev);
                            saveChartDataPrev(item.date, newDataPrev);
                          },
                          selectedChartDate: item.date,
                          fullWidth: fullWidth - legendsWidth,
                        }}
                      />
                    ) : (
                      <BarChart
                        {...{
                          ...props,
                          data: item.data,
                          setData: (newData) => {
                            props.setData(newData);
                            saveChartData(item.date, newData);
                          },
                          selectedChartDate: item.date,
                          renderLegends: false,
                          chartHeight,
                          chartWidth,
                          showInitialLoader,
                          loaderContainerStyles,
                        }}
                      />
                    )}
                  </View>
                )}
                horizontal={true}
                pagingEnabled={true}
                initialScrollIndex={selectedFlatListIndex !== null ? selectedFlatListIndex : undefined}
                keyExtractor={(item) => item.date}
                getItemLayout={(_, index) => ({
                  index,
                  length: flatListItemWidth - legendsWidth,
                  offset: index * (flatListItemWidth - legendsWidth),
                })}
                maxToRenderPerBatch={2}
                initialNumToRender={2}
                windowSize={2}
                inverted={true}
                onMomentumScrollEnd={(e) => userStopScroll(e.nativeEvent.contentOffset.x)}
                showsHorizontalScrollIndicator={false}
                maintainVisibleContentPosition={{ minIndexForVisible: 0 }}
              />
              {readyToDisplayLegends && (
                <ChartLegends
                  C={C}
                  chartHeight={chartHeight}
                  dataBar={dataBar}
                  heightValues={heightValues}
                  isDay={isDay}
                  legendsWidth={legendsWidth}
                  legendVerticalOffsets={{
                    top: 4,
                    middle: 5,
                    bottom: 12,
                  }}
                />
              )}
            </View>
          )}
        </View>
      </View>
    </View>
    </TourTarget>
  );
};

export default connect(null, { setChartType })(Chart);
